import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useSearchParams } from 'react-router-dom';
import {
  aitoolsApi,
  AIToolCard as AIToolCardData,
  AIToolCategory,
} from '../services/aitoolsApi';
import AIToolCard from '../components/aitools/AIToolCard';

type SortKey = 'latest' | 'trending' | 'top';

const SORTS: { key: SortKey; label: string }[] = [
  { key: 'latest', label: 'Latest' },
  { key: 'trending', label: 'Trending' },
  { key: 'top', label: 'Top' },
];

const normalizeSort = (v: string | null): SortKey =>
  v === 'trending' || v === 'top' ? v : 'latest';

const CardSkeleton: React.FC = () => (
  <div className="bg-white border border-slate-200 rounded-xl p-4">
    <div className="flex items-start gap-3">
      <div className="w-11 h-11 rounded-lg bg-slate-200 animate-pulse flex-shrink-0" />
      <div className="flex-1 space-y-2">
        <div className="h-3.5 w-2/3 bg-slate-200 rounded animate-pulse" />
        <div className="h-3 w-full bg-slate-100 rounded animate-pulse" />
        <div className="h-3 w-1/2 bg-slate-100 rounded animate-pulse" />
      </div>
    </div>
    <div className="mt-4 flex gap-2">
      <div className="h-4 w-16 bg-slate-100 rounded-full animate-pulse" />
      <div className="h-4 w-12 bg-slate-100 rounded-full animate-pulse" />
    </div>
  </div>
);

const AIToolsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const category = searchParams.get('category') || 'all';
  const sort = normalizeSort(searchParams.get('sort'));
  const q = searchParams.get('q') || '';

  const [searchInput, setSearchInput] = useState(q);
  const [categories, setCategories] = useState<AIToolCategory[]>([]);
  const [tools, setTools] = useState<AIToolCardData[]>([]);
  const [featured, setFeatured] = useState<AIToolCardData[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep local search box in sync when URL q changes externally (back/forward).
  useEffect(() => {
    setSearchInput(q);
  }, [q]);

  // Load categories once.
  useEffect(() => {
    aitoolsApi
      .categories()
      .then(setCategories)
      .catch(() => setCategories([]));
  }, []);

  // Load tools whenever filters change.
  useEffect(() => {
    let active = true;
    setLoading(true);
    aitoolsApi
      .list({ category, sort, q: q || undefined, page: 1 })
      .then((res) => {
        if (!active) return;
        setTools(res.tools);
        setFeatured(res.featured ?? []);
        setPage(res.page);
        setHasMore(res.has_more);
        setLoading(false);
      })
      .catch(() => {
        if (!active) return;
        setTools([]);
        setFeatured([]);
        setHasMore(false);
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [category, sort, q]);

  const updateParams = (mutate: (p: URLSearchParams) => void) => {
    const next = new URLSearchParams(searchParams);
    mutate(next);
    setSearchParams(next, { replace: false });
  };

  const onSearchChange = (value: string) => {
    setSearchInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      updateParams((p) => {
        if (value.trim()) p.set('q', value.trim());
        else p.delete('q');
      });
    }, 400);
  };

  const selectCategory = (slug: string) => {
    updateParams((p) => {
      if (slug === 'all') p.delete('category');
      else p.set('category', slug);
    });
  };

  const selectSort = (key: SortKey) => {
    updateParams((p) => {
      if (key === 'latest') p.delete('sort');
      else p.set('sort', key);
    });
  };

  const loadMore = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const res = await aitoolsApi.list({
        category,
        sort,
        q: q || undefined,
        page: page + 1,
      });
      setTools((prev) => {
        const seen = new Set(prev.map((t) => t.slug));
        return [...prev, ...res.tools.filter((t) => !seen.has(t.slug))];
      });
      setPage(res.page);
      setHasMore(res.has_more);
    } catch {
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>AI Tools Directory — SaveDelete</title>
        <meta
          name="description"
          content="Discover and submit the best AI tools — curated, categorized, and updated."
        />
        <link rel="canonical" href="https://savedelete.com/ai-tools/" />
        <meta property="og:title" content="AI Tools Directory — SaveDelete" />
        <meta
          property="og:description"
          content="Discover and submit the best AI tools — curated, categorized, and updated."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://savedelete.com/ai-tools/" />
        <meta name="twitter:card" content="summary" />
      </Helmet>

      <main className="max-w-7xl mx-auto px-4 py-5 md:py-6">
        {/* Hero */}
        <header className="mb-6 pb-5 border-b border-slate-200">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-slate-900">AI Tools Directory</h1>
              <p className="mt-2 text-sm text-slate-600 max-w-2xl">
                Discover and submit the best AI tools — curated, categorized, and updated.
              </p>
            </div>
            <Link
              to="/ai-tools/submit"
              className="inline-flex items-center justify-center px-4 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap flex-shrink-0"
            >
              + Submit a tool
            </Link>
          </div>

          {/* Search */}
          <div className="mt-5 relative max-w-xl">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true">
              🔍
            </span>
            <input
              type="search"
              value={searchInput}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Search AI tools…"
              aria-label="Search AI tools"
              className="w-full pl-9 pr-4 py-2.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </header>

        {/* Category chips */}
        <div className="flex flex-wrap gap-2 mb-4">
          <button
            type="button"
            onClick={() => selectCategory('all')}
            className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
              category === 'all'
                ? 'bg-blue-600 text-white border-blue-600'
                : 'bg-white text-slate-600 border-slate-200 hover:border-blue-300'
            }`}
          >
            All
          </button>
          {categories.map((c) => (
            <button
              key={c.slug}
              type="button"
              onClick={() => selectCategory(c.slug)}
              className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
                category === c.slug
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-blue-300'
              }`}
            >
              {c.icon ? `${c.icon} ` : ''}
              {c.name}
              <span className="ml-1 opacity-60">{c.tool_count}</span>
            </button>
          ))}
        </div>

        {/* Sort tabs */}
        <div className="flex items-center gap-1 mb-6 border-b border-slate-200">
          {SORTS.map((s) => (
            <button
              key={s.key}
              type="button"
              onClick={() => selectSort(s.key)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                sort === s.key
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>

        {/* Featured section — only when the API returns featured tools. */}
        {!loading && featured.length > 0 && (
          <section className="mb-8">
            <h2 className="flex items-center gap-1.5 text-sm font-bold text-slate-900 mb-3">
              <span aria-hidden="true">⭐</span> Featured
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {featured.map((t) => (
                <AIToolCard key={`featured-${t.slug}`} tool={t} />
              ))}
            </div>
          </section>
        )}

        {/* Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <CardSkeleton key={i} />
            ))}
          </div>
        ) : tools.length > 0 ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {tools.map((t) => (
                <AIToolCard key={t.slug} tool={t} />
              ))}
            </div>
            {hasMore && (
              <button
                type="button"
                onClick={loadMore}
                disabled={loadingMore}
                className="mt-6 w-full px-4 py-2.5 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors disabled:opacity-50"
              >
                {loadingMore ? 'Loading…' : 'Load more'}
              </button>
            )}
          </>
        ) : (
          <div className="py-12 text-center text-slate-500 text-sm border border-dashed border-slate-200 rounded-lg">
            No AI tools found{q ? ` for “${q}”` : ''}. Try a different search or category.
          </div>
        )}
      </main>
    </>
  );
};

export default AIToolsPage;
